import { useMemo } from 'react';

import type { MenuItemCustomizationOptionGroup } from '../types';

type CustomizationGroupDataType = MenuItemCustomizationOptionGroup;

type CustomizationSelections = Record<string, string | Record<string, number>>;

export function useCustomizationLimits(
  customizationGroup: CustomizationGroupDataType,
  customizationSelections: CustomizationSelections,
  isCustomizationLimitEnabled: boolean
) {
  // maxAllowed is expected to possibly be `undefined` we do not want to default it if it's undefined.
  // undefined means NO MAXIMUM
  const maxAllowed: number | undefined =
    customizationGroup.displayGroup?.numberOfCustomizationsAllowed ?? undefined;

  const numberOfUsedCustomizations = useMemo(() => {
    return customizationGroup.options.reduce((total, option) => {
      const selectedKey = customizationSelections[option.key];
      const allChoices = option.options || [];

      const displayType = option.displayType?.toUpperCase();

      switch (displayType) {
        case 'CHECKBOX': {
          const selectedOption = allChoices.find(o => o.key === selectedKey);

          if (selectedOption && !selectedOption.isDefault && selectedOption.multiplier !== 0) {
            return total + 1;
          }

          return total;
        }

        case 'STEPPER': {
          const defaultIndex = allChoices.findIndex(o => o.isDefault);
          const selectedIndex = allChoices.findIndex(o => o.key === selectedKey);

          if (defaultIndex !== -1 && selectedIndex > defaultIndex) {
            return total + 1;
          }
          return total;
        }

        default: {
          return total;
        }
      }
    }, 0);
  }, [customizationGroup, customizationSelections]);

  const isDisabled = (optionKey: string): boolean => {
    if (!isCustomizationLimitEnabled || typeof maxAllowed !== 'number') {
      return false;
    }
    const option = customizationGroup.options.find(o => o.key === optionKey);

    const displayType = option?.displayType?.toUpperCase();

    const allChoices = option?.options || [];
    const selectedKey = customizationSelections[optionKey];

    switch (displayType) {
      case 'CHECKBOX': {
        const selectedOption = allChoices.find(o => o.key === selectedKey);

        return Boolean(
          selectedOption &&
            selectedOption.isDefault &&
            selectedOption.multiplier === 0 &&
            numberOfUsedCustomizations >= maxAllowed
        );
      }

      case 'STEPPER': {
        const selectedIndex = allChoices.findIndex(o => o.key === selectedKey);
        const defaultIndex = allChoices.findIndex(o => o.isDefault);
        return selectedIndex === defaultIndex && numberOfUsedCustomizations >= maxAllowed;
      }

      default: {
        return false;
      }
    }
  };
  return { isDisabled, maxAllowed };
}
